import React, { useState } from 'react';
import { apiClient } from '../../api/client';
import {
  X, Plus, Trash2, GripVertical, ChevronUp, ChevronDown,
  Loader2, AlertTriangle,
} from 'lucide-react';

// Fallback when the agents list hasn't loaded; tools then show once it has.
const BUILT_IN_AGENTS = [
  { id: 'triage', name: 'Triage' },
  { id: 'investigator', name: 'Investigator' },
  { id: 'hunter', name: 'Threat Hunter' },
  { id: 'responder', name: 'Responder' },
  { id: 'reporter', name: 'Reporter' },
  { id: 'containment', name: 'Containment' },
  { id: 'forensics', name: 'Forensics' },
  { id: 'threat-intel', name: 'Threat Intel' },
  { id: 'vulnerability-scanner', name: 'Vuln Scanner' },
  { id: 'compliance', name: 'Compliance' },
  { id: 'notification', name: 'Notification' },
  { id: 'confidential-notification', name: 'Confidential Notification' },
  { id: 'approval', name: 'Approval Gate' },
  { id: 'enrichment', name: 'Enrichment' },
  { id: 'correlation', name: 'Correlation' },
];

const AGENT_COLORS = {
  triage: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  investigator: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  hunter: 'bg-green-500/20 text-green-400 border-green-500/30',
  responder: 'bg-red-500/20 text-red-400 border-red-500/30',
  reporter: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  containment: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  forensics: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'threat-intel': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
};

function makeEmptyPhase(order) {
  return {
    phase_id: 'phase-' + Date.now() + '-' + order,
    order,
    agent_id: 'triage',
    name: '',
    purpose: '',
    steps: [''],
    approval_required: false,
  };
}

function PhaseEditor({ phase, index, total, agents, onChange, onRemove, onMoveUp, onMoveDown }) {
  const [expanded, setExpanded] = useState(true);

  const update = (field, value) => {
    onChange({ ...phase, [field]: value });
  };

  const addStep = () => {
    update('steps', [...phase.steps, '']);
  };

  const updateStep = (stepIdx, value) => {
    const newSteps = [...phase.steps];
    newSteps[stepIdx] = value;
    update('steps', newSteps);
  };

  const removeStep = (stepIdx) => {
    update('steps', phase.steps.filter((_, i) => i !== stepIdx));
  };

  const agentColor = AGENT_COLORS[phase.agent_id] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  const agentTools = agents.find(a => a.id === phase.agent_id)?.tools;

  return (
    <div className="bg-dark-900 border border-gray-700/50 rounded-xl overflow-hidden">
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-dark-800/50 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <GripVertical className="w-4 h-4 text-gray-600 shrink-0" />
        <span className="text-xs font-mono text-gray-500 w-6 shrink-0">{index + 1}</span>
        <span className={'px-2 py-0.5 rounded text-xs font-medium border ' + agentColor}>
          {phase.agent_id}
        </span>
        <span className="text-sm text-white font-medium flex-1 truncate">
          {phase.name || 'Untitled Phase'}
        </span>
        {phase.approval_required && (
          <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
            Approval
          </span>
        )}
        <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => onMoveUp(index)}
            disabled={index === 0}
            className="p-1 rounded hover:bg-dark-700 text-gray-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronUp className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onMoveDown(index)}
            disabled={index === total - 1}
            className="p-1 rounded hover:bg-dark-700 text-gray-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onRemove(index)}
            className="p-1 rounded hover:bg-dark-700 text-gray-500 hover:text-red-400 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-700/30 pt-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1.5">
                Agent *
              </label>
              <select
                value={phase.agent_id}
                onChange={e => update('agent_id', e.target.value)}
                className="w-full bg-dark-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500"
              >
                {agents.map(a => (
                  <option key={a.id} value={a.id}>{a.name}{a.source === 'custom' ? ' (custom)' : ''}</option>
                ))}
              </select>
              {agentTools && (
                <p className="text-[11px] text-gray-500 mt-1 font-mono truncate" title="Tools come with the agent. For other tools, create a custom agent.">
                  {agentTools.length ? agentTools.join(', ') : 'No tools'}
                </p>
              )}
            </div>
            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1.5">
                Phase Name *
              </label>
              <input
                type="text"
                value={phase.name}
                onChange={e => update('name', e.target.value)}
                placeholder="e.g., Triage, Containment"
                className="w-full bg-dark-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1.5">
              Purpose
            </label>
            <input
              type="text"
              value={phase.purpose}
              onChange={e => update('purpose', e.target.value)}
              placeholder="What this phase accomplishes"
              className="w-full bg-dark-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1.5">
              Steps
            </label>
            <div className="space-y-2">
              {phase.steps.map((step, stepIdx) => (
                <div key={stepIdx} className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-600 w-5 shrink-0">{stepIdx + 1}.</span>
                  <input
                    type="text"
                    value={step}
                    onChange={e => updateStep(stepIdx, e.target.value)}
                    placeholder={'Step ' + (stepIdx + 1)}
                    className="flex-1 bg-dark-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-orange-500"
                  />
                  {phase.steps.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeStep(stepIdx)}
                      className="p-1 rounded hover:bg-dark-700 text-gray-600 hover:text-red-400 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addStep}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-orange-400 transition-colors"
              >
                <Plus className="w-3 h-3" /> Add step
              </button>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={phase.approval_required}
              onChange={e => update('approval_required', e.target.checked)}
              className="w-4 h-4 rounded border-gray-700 bg-dark-800 text-orange-500 focus:ring-orange-500"
            />
            <span className="text-sm text-gray-300">Require human approval before executing</span>
          </label>
        </div>
      )}
    </div>
  );
}

export default function WorkflowBuilder({ workflow, agents, onClose, onSave }) {
  const isEditing = !!(workflow && workflow.id);
  const agentOptions = agents && agents.length ? agents : BUILT_IN_AGENTS;

  const [form, setForm] = useState({
    name: (workflow && workflow.name) || '',
    description: (workflow && workflow.description) || '',
    use_case: (workflow && workflow.use_case) || '',
    trigger_examples: (workflow && workflow.trigger_examples) ? workflow.trigger_examples.join('\n') : '',
    phases: (workflow && workflow.phases && workflow.phases.length > 0)
      ? workflow.phases.map((p, i) => ({ ...p, order: i }))
      : [makeEmptyPhase(0)],
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const addPhase = () => {
    setForm(prev => ({
      ...prev,
      phases: [...prev.phases, makeEmptyPhase(prev.phases.length)],
    }));
  };

  const updatePhase = (index, phase) => {
    setForm(prev => {
      const phases = [...prev.phases];
      phases[index] = phase;
      return { ...prev, phases };
    });
  };

  const removePhase = (index) => {
    setForm(prev => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== index).map((p, i) => ({ ...p, order: i })),
    }));
  };

  const movePhase = (from, to) => {
    setForm(prev => {
      const phases = [...prev.phases];
      const moved = phases.splice(from, 1)[0];
      phases.splice(to, 0, moved);
      return { ...prev, phases: phases.map((p, i) => ({ ...p, order: i })) };
    });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Workflow name is required';
    if (form.phases.length === 0) errs.phases = 'At least one phase is required';
    form.phases.forEach((p, i) => {
      if (!p.name.trim()) errs['phase_' + i + '_name'] = 'Phase ' + (i + 1) + ' name is required';
    });
    return errs;
  };

  const handleSubmit = async () => {
    setError(null);

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setValidationErrors(errs);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        use_case: form.use_case.trim() || null,
        trigger_examples: form.trigger_examples
          .split('\n')
          .map(s => s.trim())
          .filter(Boolean),
        agents: [...new Set(form.phases.map(p => p.agent_id))],
        // Tools come from each phase's agent; drop any an older playbook still carries.
        phases: form.phases.map(({ tools, ...p }, i) => ({
          ...p,
          order: i,
          steps: p.steps.filter(s => s.trim()),
        })),
      };

      if (isEditing) {
        await apiClient.put('/api/workflows/' + workflow.id, payload);
      } else {
        await apiClient.post('/api/workflows', payload);
      }

      onSave();
    } catch (err) {
      setError(
        (err && err.response && err.response.data && err.response.data.error) ||
        (err && err.message) ||
        'Failed to save workflow'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-dark-800 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between p-6 border-b border-gray-700 shrink-0">
          <div>
            <h2 className="text-white font-bold text-lg">
              {isEditing ? 'Edit Workflow' : 'New Workflow'}
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              Define phases, agents, and execution steps
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-dark-700 text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <div className="space-y-4">
            <h3 className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Workflow Details</h3>

            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1.5">
                Name *
              </label>
              <input
                type="text"
                value={form.name}
                onChange={e => set('name', e.target.value)}
                placeholder="e.g., Incident Response - Ransomware"
                className={'w-full bg-dark-900 border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500 ' + (
                  validationErrors.name ? 'border-red-500/50' : 'border-gray-700'
                )}
              />
              {validationErrors.name && (
                <p className="text-xs text-red-400 mt-1">{validationErrors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1.5">
                Description
              </label>
              <textarea
                value={form.description}
                onChange={e => set('description', e.target.value)}
                placeholder="What this workflow does and when to use it"
                rows={2}
                className="w-full bg-dark-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500 resize-none"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1.5">
                Use Case
              </label>
              <input
                type="text"
                value={form.use_case}
                onChange={e => set('use_case', e.target.value)}
                placeholder="e.g., A critical alert needs full incident response"
                className="w-full bg-dark-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500"
              />
            </div>

            <div>
              <label className="block text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1.5">
                Trigger Examples (one per line)
              </label>
              <textarea
                value={form.trigger_examples}
                onChange={e => set('trigger_examples', e.target.value)}
                placeholder={'Run incident response on finding f-20260215-abc123\nInvestigate case case-2026-0142'}
                rows={2}
                className="w-full bg-dark-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500 resize-none font-mono"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                Phases ({form.phases.length})
              </h3>
              <button
                type="button"
                onClick={addPhase}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-dark-700 border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 text-xs font-medium transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Add Phase
              </button>
            </div>
            {validationErrors.phases && (
              <p className="text-xs text-red-400">{validationErrors.phases}</p>
            )}

            <div className="space-y-3">
              {form.phases.map((phase, idx) => (
                <PhaseEditor
                  key={phase.phase_id}
                  phase={phase}
                  index={idx}
                  total={form.phases.length}
                  agents={agentOptions}
                  onChange={(updated) => updatePhase(idx, updated)}
                  onRemove={removePhase}
                  onMoveUp={(i) => movePhase(i, i - 1)}
                  onMoveDown={(i) => movePhase(i, i + 1)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between shrink-0">
          <p className="text-xs text-gray-500">
            {form.phases.length} phase{form.phases.length !== 1 ? 's' : ''} &middot;{' '}
            {form.phases.reduce((acc, p) => acc + p.steps.filter(s => s.trim()).length, 0)} steps
          </p>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-dark-700 text-gray-300 text-sm hover:bg-dark-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isEditing ? 'Save Changes' : 'Create Workflow'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
